/* -----------------------------------------------------------------------------------------------------
  - Created: [31-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD]
  - Description: This file contains Project list and add/edit/delete modals
  -------------------------------------------------------------------------------------------------------*/

import React, { useEffect, useState } from 'react'
import ProjectManagementSearch from '../ProjectManagementSearch/ProjectManagementSearch';
import './ProjectManagementTable.css'
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal'
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import ProjectManagementForm from '../ProjectManagementForm/ProjectManagementForm';
import { get_project_by_id, get_project_with_pagination, project_creation, project_update } from '../../../../config/Worksite/projectApis';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { useSelector } from 'react-redux';
import { hasPrivilege } from '../../../../utils/privileges';
import Loader from '../../../../Components/Loader/Loader';
import { Icons } from '../../../../utils/GlobalIcons';
import ActionHistory from '../../../../Components/ActionHistory/ActionHistory';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';
import { getStatusColors } from '../../../../utils/constants';
import { fetchHistoryData } from '../../../../config/HistoryService/historyService';

function ProjectManagementTable() {

    const privileges = useSelector((state) => state.privileges.privileges);

    // State variables for managing project data, pagination, and modal states
    const [Listing, SetListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [modalProps, setModalProps] = useState({ isOpen: false, id: '', formValues: {} });

    const [filterValues, setFilterValues] = useState({}); // Define filter values state
    const [isLoading, setIsLoading] = useState(false); // Loader state

    const [showHistory, setShowHistory] = useState(false); // history modal state
    const [historyData, setHistoryData] = useState([]);
    const [historyLoader, setHistoryLoader] = useState(false); // Loader state

    // Fetch project data on page change or refresh
    useEffect(() => {
        fetchProjects()
    }, [pageNumber, totalItemPerPage, filterValues, refresh]);


    // Fetch project data from API with filter values
    const fetchProjects = async () => {

        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        // Append filter values to the formData 
        Object.keys(filterValues)?.forEach(key => {
            formData.append(key, filterValues[key]);
        });

        try {
            setIsLoading(true)
            const response = await get_project_with_pagination(formData);
            if (response?.status === "success") {
                const data = response.data;
                SetListing(data.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                SetListing([]);
            }
        } catch (error) {
            console.log("error", error);
            SetListing([]);
        } finally {
            setIsLoading(false)
        }
    };


    //------------New code after privielegae uing id
    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Work site", 1, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Work site", 1, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Work site", 1, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Work site", 1, PERMISSION_ACTIONS.add),
    };
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;
    //-------------------END

    // Columns for the table
    const columns = [
        { title: "#", key: "key" },

        { title: "Name", key: "ProjectName" },
        {
            title: "Project Type",
            key: "ProjectType",
            render: (ProjectType) => (
                <span >
                    {ProjectType == 1 ? 'One Time' : 'Contract'}
                </span>
            ),
        },
        { title: "Code", key: "ProjectCode" },
        { title: "Client", key: "ClientName" },
        { title: "Start Date", key: "ProjectStartdate" },
        { title: "End Date", key: "ProjectEnddate" },

        {
            title: "Status",
            key: "StatusName",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.StatusName || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },

        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false


    // Render action icons for each project item
    const renderActions = (row) => (
        <div className="action-container">
            {userPermissions.view &&
                <CustomTooltipWithoutRoundBorder title="View ">
                    <Icons.View className="view-action-icon" onClick={() => handleModal(true, row?.id, 'view')} />
                </CustomTooltipWithoutRoundBorder>
            }

            {userPermissions.edit &&
                <CustomTooltipWithoutRoundBorder title="Edit">
                    <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>
            }

            {userPermissions.history && <CustomTooltipWithoutRoundBorder title="History">
                <Icons.History className="history-action-icon" onClick={() => handleHistoryModal(true, row?.id)} />
            </CustomTooltipWithoutRoundBorder>}

        </div>
    );

    // Map the project data into table rows
    const rows = () => Listing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.ProjectID,
        ProjectName: item.ProjectName,
        ProjectCode: item.ProjectCode,
        StatusName: item?.StatusName,
        ProjectStartdate: item?.ProjectStartdate,
        ProjectEnddate: item?.ProjectEnddate,
        ClientName: item?.ClientName,
        ProjectType: item?.ProjectType,
    }));

    // Open Add/Edit/View modal
    const handleModal = async (isOpen, projectId, actionType) => {

        if (actionType === 'view' && projectId) { //FOR VIEW
            await fetchProjectDetails(projectId,);
            setIsViewModalOpen(true);
        } else if (projectId) { //FOR EDIT
            await fetchProjectDetails(projectId);
        } else { //FOR ADD
            setModalProps({
                isOpen,
                id: '',
                formValues: {
                    PD_ProjectName: '',
                    PD_Code: '',
                    PD_StartDate: null,
                    PD_EndDate: null,
                    PD_Type: 0,
                    PD_ClientName: '',
                    PD_Status: 1,
                    PD_Description: ''

                }
            });
        }

    };

    // Fetch details of a specific project item
    const fetchProjectDetails = async (projectId, actionType) => {
        try {
            const response = await get_project_by_id({ projectID: projectId });
            if (response?.status === "success") {
                const data = response?.data;
                setModalProps({
                    isOpen: actionType === 'view' ? false : true,
                    id: data?.PD_Id,
                    formValues: {
                        PD_ProjectName: data?.PD_ProjectName,
                        PD_Code: data?.PD_Code,
                        PD_Description: data?.PD_Description,
                        PD_ClientName: data?.PD_ClientName,
                        PD_Status: data?.PD_Status,
                        PD_StartDate: data?.PD_StartDate,
                        PD_EndDate: data?.PD_EndDate,
                        PD_Type: data?.ProjectType,
                    },
                });
            } else {
                ErrorToast(response?.message || 'Something went wrong!');
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        }
    };

    // Handle saving project data (add/edit)
    const handleProjectSave = async (values, projectId) => {
        try {
            setIsButtonDisabled(true);
            const apiFunction = projectId ? project_update : project_creation;
            if (projectId) values.projectID = projectId;

            const response = await apiFunction(values);
            const message = response?.data?.message || 'Something went wrong!';

            if (response?.data?.status === "success") {
                SuccessToast(message);
                closeModal();
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            console.log("error2", error);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // Close modal
    const closeModal = () => {
        setModalProps({ ...modalProps, isOpen: false });
        setIsViewModalOpen(false);
    };

    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);


    // Handle search filter changes and update filter values
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    const handleHistoryModal = async (open, ProjectId) => {
        setShowHistory(open);

        if (open) {
            setHistoryLoader(true);
            try {
                const response = await fetchHistoryData('project', ProjectId);
                if (response?.status === "success") {
                    //  Transform API response for clean table usage
                    const formatted = response?.data?.map((item, index) => ({
                        id: item.id,
                        projectName: item.projectname || "-",
                        changedBy: item.changed_by_name || "Unknown",
                        changedAt: item.changed_at || "-",
                        changeDescription: item.change_description || "-",
                    }));

                    setHistoryData(formatted || []);
                } else {
                    // InfoToast("No history data found.");
                    setHistoryData([]);
                }
            } catch (error) {
                console.error("Error fetching project history:", error);
            } finally {
                setHistoryLoader(false);
            }
        } else {
            setHistoryData([]);
        }
    };

    return (
        <div className='project-management-table-container'>
            <ProjectManagementSearch
                handleAddEditModal={handleModal}
                tableTitle={`Projects`}
                isAddEditModalOpen={modalProps.isOpen}
                searchFilter={handleSearchFilter}
                isAddPermission={userPermissions?.add}

                refresh={refresh}
                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}
            />

            <div>
                <CustomTable columns={columns} rows={isLoading ? [] : rows()} renderActions={renderActions} isLoading={isLoading} />
            </div>
            <div className='bottom-container'>
                {isLoading ? <Loader /> : <CustomBottomPagination
                    items_per_page={totalItemPerPage}
                    current_page={pageNumber}
                    total_page_count={totalPages}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                    pagination={pagination}

                />}
            </div>

            {/* Custom Add/Edit Project Modal */}
            <CustomModal
                open={modalProps.isOpen}
                handleClose={closeModal}
                title={isViewModalOpen ? "View Project" : modalProps.id ? "Edit Project" : "Create Project"}

                body={<ProjectManagementForm

                    isDisabled={isViewModalOpen}
                    isButtonDisabled={isButtonDisabled}
                    id={modalProps.id}
                    formValues={modalProps.formValues}
                    handleSave={handleProjectSave}

                />}
                maxWidth="700px"
            />

            {showHistory && <CustomModal
                open={showHistory}
                handleClose={() => handleHistoryModal(false)}
                title="Project History"
                maxWidth="75%"
                body={
                    historyLoader ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Loader />
                        </div>
                    ) : (
                        <ActionHistory
                            title=""
                            data={historyData}
                            columns={[
                                { key: "projectName", label: "Project Name" },
                                { key: "changedBy", label: "Changed By" },
                                { key: "changedAt", label: "Changed At" },
                                { key: "changeDescription", label: "Change Description" },
                            ]}
                            noDataMessage="No change history available."
                        />

                    )
                }
            />}
        </div>
    );
}

export default ProjectManagementTable;
